import React from "react";
import MarketingLayout, { SpaLink } from "./MarketingLayout";

const FAQ_GROUPS = [
  {
    heading: "Getting started",
    items: [
      {
        q: "What is GearCheckAuto?",
        a: "GearCheckAuto helps vehicle owners connect with nearby auto repair shops and mobile mechanics. You describe your problem once, send a single request to several local shops, then compare quotes and response times and book the best fit.",
      },
      {
        q: "How does it work?",
        a: "Tell us about your vehicle and the issue, send one request to nearby shops and mobile mechanics, then compare their quotes and response times and choose the one that fits your budget and schedule.",
      },
      {
        q: "Does it cost anything to send a request?",
        a: "Creating an account and sending a request to shops is free for vehicle owners. You only pay the shop you choose for the work you agree to.",
      },
    ],
  },
  {
    heading: "Repairs",
    items: [
      {
        q: "How do I get my car repaired through GearCheckAuto?",
        a: "Describe the symptoms and send one request to several nearby shops. Our request coach helps you add the details shops need to give an accurate quote, so you can compare real options instead of calling around.",
      },
      {
        q: "Can I compare repair quotes from different shops?",
        a: "Yes. That is the point. One request goes to multiple shops, and you compare their quotes and response times side by side before you book.",
      },
    ],
  },
  {
    heading: "Pre-purchase inspections",
    items: [
      {
        q: "What is a pre-purchase inspection?",
        a: "A pre-purchase inspection is a thorough check of a used car by a qualified mechanic before you buy it, covering the engine, brakes, suspension, frame, and electronics so you know the car's true condition.",
      },
      {
        q: "How do I get a pre-purchase inspection?",
        a: "Tell us the vehicle you are considering and where you are, send one request to nearby shops and mobile mechanics, then compare quotes and book the inspection that fits you best.",
      },
    ],
  },
  {
    heading: "Mobile mechanics",
    items: [
      {
        q: "Can a mechanic come to me?",
        a: "Yes. Mobile mechanics on GearCheckAuto can come to your home or workplace for many common repairs, diagnostics, and pre-purchase inspections. They show the area they serve on the map.",
      },
    ],
  },
  {
    heading: "For shops",
    items: [
      {
        q: "I run a repair shop. Can I receive requests?",
        a: "Yes. Shops and mobile mechanics can register to receive nearby vehicle requests and respond with quotes. Sign up to get started or contact us with questions.",
      },
    ],
  },
];

const JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_GROUPS.flatMap((group) =>
      group.items.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      }))
    ),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://gearcheckauto.com/" },
      { "@type": "ListItem", position: 2, name: "FAQ", item: "https://gearcheckauto.com/faq" },
    ],
  },
];

export default function FaqPage({ onNavigate }) {
  return (
    <MarketingLayout onNavigate={onNavigate} jsonLd={JSON_LD}>
      <section className="bg-gradient-to-b from-blue-50 dark:from-blue-950/40 to-white px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <h1 className="text-4xl font-bold leading-tight tracking-tight text-slate-900 dark:text-gray-100 md:text-5xl">
            Frequently asked questions
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-gray-300">
            Answers about auto repair requests, pre-purchase inspections, mobile mechanics, and how
            GearCheckAuto works for vehicle owners and shops.
          </p>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-4 py-12 text-slate-700 dark:text-gray-200">
        {FAQ_GROUPS.map((group) => (
          <section key={group.heading} className="mb-10">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">{group.heading}</h2>
            <div className="mt-4 space-y-5">
              {group.items.map((item) => (
                <div key={item.q}>
                  <p className="font-semibold text-slate-900 dark:text-gray-100">{item.q}</p>
                  <p className="mt-1 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </section>
        ))}

        <p className="leading-relaxed">
          Learn more about{" "}
          <SpaLink view="prePurchaseInspection" onNavigate={onNavigate} className="font-semibold text-blue-700 dark:text-blue-300 hover:text-blue-600">
            pre-purchase inspections
          </SpaLink>{" "}
          and{" "}
          <SpaLink view="autoRepair" onNavigate={onNavigate} className="font-semibold text-blue-700 dark:text-blue-300 hover:text-blue-600">
            getting your car repaired
          </SpaLink>
          .
        </p>
      </article>

      <section className="bg-slate-900 px-4 py-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-white">Still have questions?</h2>
          <p className="mt-3 text-slate-300">Get started in a couple of minutes, or reach out and we will help.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => onNavigate("signup")}
              className="rounded-lg bg-red-600 px-7 py-3 text-base font-semibold text-white hover:bg-red-500"
            >
              Get started
            </button>
            <button
              type="button"
              onClick={() => onNavigate("contact")}
              className="rounded-lg border border-slate-500 px-7 py-3 text-base font-semibold text-white hover:bg-slate-800"
            >
              Contact us
            </button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
